import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Input,
	TextArea,
	Select,
	Button,
	Slider,
	DragDrop,
	Popover,
	Toast,
	Avatar,
	CountryDropdown,
} from '@kaleyra/emmy';

import InfoAlert from '../../../../../../assets/InfoAlert.svg';

import { NumberType, NumberFieldNameType, NumberResponseType } from '../../../../../../interface';

import Styles from './index.module.scss';
import { isEmpty } from 'lodash';
import { profileUpload } from '../../../../../../store/actions/whatsappNumbers.action';
import { useDispatch } from 'react-redux';
import { validatePhoneNumber } from '../../../../../../utils';

export type MapStatePropsType = {
	number: NumberType;
	templateData: any;
	countryList: any[];
	params: Record<string, any> | undefined;
};

export type DispatchPropsType = {
	onChange: (fields: { name: NumberFieldNameType; value: string }) => void;
	onSave: (reqObj: any, callback: (resp: any, flag: boolean) => void) => void;
	onLoad: () => void;
	fetchCountryList: () => void;
	loadNumbers: () => void;
	uploadDocument: (document: any) => void;
	onClose: () => void;
};

export type OptionType = {
	label: string;
	value: string;
};

const NumberForm: React.FC<MapStatePropsType & DispatchPropsType> = ({
	number,
	templateData,
	countryList,
	params,
	onSave,
	onLoad,
	fetchCountryList,
	uploadDocument,
	loadNumbers,
	onChange,
	onClose,
}) => {
	const { t } = useTranslation();
	const [image, setImage] = useState<string>('')
	const [imageFile, setImageFile] = useState<any>();
	const [countries, setCountries] = useState<any>([]);
	const dispatch = useDispatch();

	if (number.profile_image) {
		setImage(number.profile_image);
	}


	useEffect(() => {
		if (Array.isArray(countryList)) {
			let country = countryList.map((item: any) => ({
				"countryid": item.country_id,
				"name": item.name,
				"code": item.code,
				"isoCode": item.iso_code
			}))
			setCountries([...country])
		}
	}, [countryList])

	useEffect(() => {
		onLoad()
		fetchCountryList()
	}, [])

	const onLocalChange = (
		name: NumberFieldNameType,
		isCustom: boolean = false
	) => (value: any) => {
		onChange({
			name,
			value: isCustom
				? (value as React.ChangeEvent<
					HTMLInputElement | HTMLTextAreaElement
				>).target.value
				: value,
		});
	};

	const onNumberChange = (
		name: NumberFieldNameType,
		isCustom: boolean = false
	) => (value: any, code: string) => {
		if (code !== number.country_code) {
			let countryDetails = countries.find((item: any) => item.code === code);
			if (countryDetails) {
				onChange({ name: 'country_code', value: countryDetails.isoCode })
				onChange({ name: 'country_dialcode', value: countryDetails.code })
			}
		}
		onChange({
			name,
			value: isCustom
				? (value as React.ChangeEvent<
					HTMLInputElement | HTMLTextAreaElement
				>).target.value
				: value,
		});
	};

	const getLabels = (key: string): Record<string, any> => {
		try {
			switch (key.toLowerCase()) {

				case "category":
					return { ...templateData?.categories };
				default:
					return {};
			}
		}
		catch (error) {
			console.error(error);
			return {};
		}
	}

	const getOptions = (key: string): OptionType[] => {
		let options: OptionType[] = [];
		let keyOptions: Record<string, any> = { ...getLabels(key) };

		Object.keys(keyOptions).forEach((key: string) => {
			if (key)
				options.push({
					label: keyOptions[key],
					value: key
				});
		})
		return options;
	}

	const onFileSelect = (value: any, isValid: boolean) => {
		if (isValid) {
			const [file] = value;
			let imageUrl = URL.createObjectURL(file);
			setImage(imageUrl);
			setImageFile(file);
		}
		else {
			Toast(t('F104'), 'error');
		}
	};

	const getPopOverContent = () => (
		<div className={Styles.popoverContainer}>
			<p>Your WhatsApp name which will be visible to your customers</p>
			<p>This is a one time setting</p>
			<p>Sample URL: https://example.com/incoming_url</p>
			<a
				href="https://developers.facebook.com/docs/whatsapp/guides/display-name#display-name"
				target="_blank"
				rel="noopener noreferrer"
			>
				Know More
			</a>
		</div>
	);

	const callback = (res: NumberResponseType, flag: boolean): void => {
		if (flag && res.data && isEmpty(res.error)) {
			Toast(res.message, 'success')
			onClose();
			loadNumbers();
		} else if (!isEmpty(res.error)) {
			Object.keys(res.error).forEach((key: any) => {
				Toast(res.error[key], 'error');
			});
		} else {
			Toast(res.message, 'error');
		}
	}

	const getRequestObj = () => {

		return ({
			"name": number.name,
			"mobile_number": number.phone_number,
			"country_code": number.country_code ? number.country_code : "",
			"country_dialcode": number.country_dialcode ? Number(number.country_dialcode) : undefined,
			"incoming_url": number.incoming_url ? number.incoming_url : "",
			"address": number.address ? number.address : "",
			"description": number.description ? number.description : "",
			"category": number.category ? number.category : "",
			"email": number.email ? number.email : "",
			"website": number.website ? number.website : ''
		})
	}

	const validateFields = () => {

		if (!number.name) {
			Toast(t("BLD01540"), "error");
			return false;
		}
		if (!number.phone_number) {
			Toast(t("BLD01541"), "error");
			return false;
		}
		if (number.phone_number && !validatePhoneNumber(number.phone_number)) {
			Toast(t("BLD01542"), "error");
			return false;
		}
		return true
	}

	const onError = () => {
		Toast(t('uploadFail'), 'error');
	}

	const onClickSave = () => {
		let reqObj: Record<string, any> = {}
		reqObj = getRequestObj();
		if (!validateFields()) return;
		if (imageFile) {
			const formData = new FormData();
			formData.append('document_ids', imageFile);
			dispatch(
				profileUpload(formData, (res: any) => {
					reqObj.profile_image = res?.data?.profile_picture
					onSave(reqObj, callback)
				}, onError)
			);
		}
		else {
			onSave(reqObj, callback)
		}
	}

	const getBody = () => {
		return (
			<div className={Styles.container}>
				<div className={Styles.numberContainer}>
					<p>{t('BLD00447')}</p>
					<Popover
						content={getPopOverContent()}
						placement="bottom"
						color="black"
						trigger={'click'}
						overlayStyle={{ width: '15rem' }}
					>
						<img src={InfoAlert} alt="alert" />
					</Popover>
				</div>
				<Input
					label={t('BLD1941')}
					placeholder={t('BLD1941')}
					onChange={onLocalChange('name', true)}
					value={number?.name}
				/>
				<CountryDropdown
					data={countries}
					mode="iso"
					label={t('mainMobileNumber')}
					placeholder={t('mainMobileNumber')}
					onChange={onNumberChange('phone_number', true)}
					defaultSelectValue={number?.country_dialcode ? number?.country_dialcode?.toString() : "91"}
					value={number?.phone_number}
				/>
				<Input
					label={t('BLD00452')}
					placeholder={t('BLD00452')}
					onChange={onLocalChange('incoming_url', true)}
					value={number?.incoming_url}
				/>
				<p>{t('BLD00454')}</p>
				<div className={Styles.dragDropContainer}>
					{
						image ?
							<div className='d-inline-flex'>
								<Avatar imgUrl={image} size={100} />
								<div className='w-75 p-1'>
									<DragDrop
										onChange={onFileSelect}
										fileFormats={'.jpg, .jpeg, .png'}
										trigger={<Button iconPosition="left" type="tertiary">{t('mainUploadImage')}</Button>}
									/>
									<p style={{ 'paddingLeft': '1rem' }} className={Styles.secondaryTextStyle}>{t("BLD00482")}</p>
								</div>
							</div>
							: <>
								<DragDrop
									onChange={onFileSelect}
									fileFormats={'.jpg, .jpeg, .png'}
								/>
								<p className={Styles.secondaryTextStyle}>{t("BLD00482")}</p>
							</>
					}
				</div>

				<TextArea
					// css
					placeholder={t('BLD00457')}
					value={number?.address}
					onChange={onLocalChange('address', true)}
					rows={7}
					characterLimit={256}
				/>
				<TextArea
					// css
					placeholder={t('BLD00458')}
					value={number?.description}
					onChange={onLocalChange('description', true)}
					rows={7}
					characterLimit={139}
				/>
				<Select
					label={t('BLD00459')}
					placeholder={t('BLD00459')}
					options={getOptions('category')}
					onChange={onLocalChange('category')}
					value={number?.category ? number?.category : undefined}
				/>
				<Input
					label={t('BLD00460')}
					placeholder={t('BLD00460')}
					onChange={onLocalChange('email', true)}
					value={number?.email}
				/>
				<Input
					label={t('BLD00461')}
					placeholder={t('BLD00461')}
					onChange={onLocalChange('website', true)}
					value={number?.website}
				/>
			</div>
		);
	};

	const getFooter = (): React.ReactNode => {
		return (
			<div className={Styles.footerContainer}>
				<Button type="tertiary" onClick={onClose}>
					{t('mainCancel')}
				</Button>
				<Button type="primary" onClick={onClickSave}>
					{t('mainSave')}
				</Button>
			</div>
		);
	};

	return (
		<Slider
			footer={getFooter()}
			visibility={true}
			containerId={'root-modal'}
			onClose={onClose}
			title={t('BLD1181')}
		>
			{getBody()}
		</Slider>
	);
};

export default NumberForm;
